import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import Actions from "../../../components/controls/Actions";
import Errors, { getString } from "../../../components/errors/Errors";
import CreatedModifiedInfo from "../../../components/forms/CreatedModifiedInfo";
import FormData from "../../../components/forms/FormData";
import FormLabel from "../../../components/forms/FormLabel";
import FormTable from "../../../components/forms/FormTable";
import FormTr from "../../../components/forms/FormTr";
import MultiCheckBox from "../../../components/forms/MultiCheckBox";
import Title from "../../../components/Title";
import useAbortController from "../../../hooks/useAbortController";
import useGlobalState from "../../../hooks/useGlobalState";
import useStatusBarState from "../../../hooks/useStatusBar";
import { Api } from "../../../listsSettings/api";
import { ErrorCode, IAction, UserPermissions } from "../../../services/types";
import { formatBoolean, getCloseAction, getCurrentUrlAsSource, getSourceFromUrl } from "../../../services/utils";
import { getUserPermissions } from "../../../services/webapi";
import strings from "../../../strings";
import Bin from "../../../svg/Bin.svg?react";
import Edit from "../../../svg/Edit.svg?react";
import * as Fields from "./Fields";
import { memberTypes, renderMemberType, UserMemberPermissionFull } from "./types";
import { allItems, editForm } from "./urls";
import { deleteUserMemberPermission, getUserMemberPermission } from "./webapi";

const DispForm: FC = () => {
  const setPage = useGlobalState((state) => state.setPage);
  const setListName = useGlobalState((state) => state.setListName);
  const [abortController, resetAbortController] = useAbortController();
  const [state, setState] = useState<UserMemberPermissionFull>();
  const [errorCode, setErrorCode] = useState<ErrorCode>();
  const [permissions, setPermissions] = useState<UserPermissions>();
  const statusBar = useStatusBarState();
  const navigateTo = useNavigate();
  const { id } = useParams<{ id?: string }>();

  useEffect(() => {
    id && initializeState(Number(id));

    return () => {
      abortController && abortController.abort();
    };
  }, [id]);

  const initializeState = async (id: number) => {
    const abortController = resetAbortController();
    const permissions = await getUserPermissions(
      Api.Lists.UserMemberPermissions.InternalName,
      Api.InternalName,
      abortController.signal,
    );
    if (!abortController.signal.aborted) {
      setPermissions(permissions);
      const canRead = permissions.schemaPermission && permissions.listPermissions.includes("read-all");
      !canRead && setErrorCode(403);
      if (canRead) {
        const result = await getUserMemberPermission(id, abortController.signal);
        if (!abortController.signal.aborted) {
          result.error && setErrorCode(result.error.code);
          result.data && setState(result.data);
        }
      }
    }
  };

  const onDelete = async () => {
    if (state && confirm(strings.confirmDeleteItem)) {
      const abortController = resetAbortController();
      const error = await deleteUserMemberPermission(state.user.id, abortController.signal);
      if (!abortController.signal.aborted) {
        error && statusBar.addError(getString(error));
        !error && navigateTo(getSourceFromUrl() ?? allItems);
      }
    }
  };

  const editAction: IAction = {
    title: strings.edit,
    icon: Edit,

    onClick: () => state && navigateTo(`${editForm}/${state.user.id}?${getCurrentUrlAsSource()}`),
    disabled: !permissions || !permissions.listPermissions.includes("edit-all"),
  };
  const closeAction: IAction = getCloseAction(navigateTo, allItems);
  const deleteAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: onDelete,
    disabled: !permissions || !permissions.listPermissions.includes("delete-all"),
  };

  useEffect(() => {
    if (state) {
      setListName(Api.Lists.UserMemberPermissions.Title);
      setPage(state.user.name);
    }
  }, [state]);

  return (
    <>
      {!errorCode && state && (
        <>
          <Title text={"Berechtigung Mitglieder"} />
          <Actions actions={[editAction, closeAction, deleteAction]} />
          <FormTable>
            <FormTr>
              <FormLabel>{Fields.userName.title}</FormLabel>
              <FormData disp>{state.user.name}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.userKey.title}</FormLabel>
              <FormData disp>{state.user.key}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.memberTypes.title}</FormLabel>
              <FormData>
                <MultiCheckBox
                  values={memberTypes.map((x) => ({
                    value: x,
                    label: renderMemberType(x),
                    selected: state.memberTypes.indexOf(x) >= 0,
                  }))}
                  disp
                />
              </FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.canceledMembers.title}</FormLabel>
              <FormData disp>{formatBoolean(state.canceledMembers)}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.companies.title}</FormLabel>
              <FormData disp>{formatBoolean(state.companies)}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.canceledCompanies.title}</FormLabel>
              <FormData disp>{formatBoolean(state.canceledCompanies)}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.legalRepresentative.title}</FormLabel>
              <FormData disp>{formatBoolean(state.legalRepresentative)}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.departmentData.title}</FormLabel>
              <FormData disp>{formatBoolean(state.departmentData)}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.customEndpoint.title}</FormLabel>
              <FormData disp>{formatBoolean(!!state.endpoint)}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.endpoint.title}</FormLabel>
              <FormData disp>{state.endpoint}</FormData>
            </FormTr>
          </FormTable>
          <CreatedModifiedInfo info={state} />
        </>
      )}
      {errorCode && <Errors errorCode={errorCode} />}
    </>
  );
};

export default DispForm;
